import { useTranslation } from "react-i18next";
import {
    Button,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldGroup,
    FieldLabel,
    FieldSet,
    Grid,
    Input,
    ListCard,
    ListCardRow,
} from "@athena/ui";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { CreateOrganizationDocument, UpdateOrganizationDocument } from "@/graphql/organizations.generated";

const emptyOrganization = {
    name: "",
    contactPhoneNumber: "",
    contactEmailAddress: "",
};

interface SubOrgFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The created organization is a sub-org of this parent. */
    parentOrganizationId: string;
    /** Called after the organization is created or its details are saved, so the caller can refetch its list. */
    onSaved: () => void;
}

/**
 * "New sub-organization" form. A sub-org is just an organization with
 * parentOrganizationId set — mirrors OrganizationsPage's top-level "new
 * organization" dialog, kept as a separate component (rather than shared)
 * since OrganizationsPage manages its own loading/refetch lifecycle.
 */
export function SubOrgFormDialog({ open, onOpenChange, parentOrganizationId, onSaved }: SubOrgFormDialogProps) {
    const { t } = useTranslation();
    const [
        organization,
        setOrganization,
    ] = useState(emptyOrganization);
    const [
        creating,
        setCreating,
    ] = useState(false);
    // Set once createOrganization succeeds — ImageUploader needs a real
    // organization id, which doesn't exist until the org is created, so the
    // first Save creates the org and subsequent edits (incl. the logo) update it.
    const [
        createdOrgId,
        setCreatedOrgId,
    ] = useState<string | null>(null);
    const [createOrganization] = useMutation(CreateOrganizationDocument);
    const [updateOrganization] = useMutation(UpdateOrganizationDocument);

    const closeDialog = () => {
        onOpenChange(false);
        setOrganization(emptyOrganization);
        setCreatedOrgId(null);
    };

    const handleSave = async () => {
        if (!organization.name.trim()) return;
        setCreating(true);
        try {
            if (!createdOrgId) {
                await createOrganization({
                    variables: {
                        input: {
                            name: organization.name.trim(),
                            contactPhoneNumber: organization.contactPhoneNumber.trim() || undefined,
                            contactEmailAddress: organization.contactEmailAddress.trim() || undefined,
                            chatEnabled: false,
                            parentOrganizationId,
                        },
                    },
                });
                onSaved();
                closeDialog();
                return;
            }

            await updateOrganization({
                variables: {
                    id: createdOrgId,
                    input: {
                        name: organization.name.trim(),
                        contactPhoneNumber: organization.contactPhoneNumber.trim() || undefined,
                        contactEmailAddress: organization.contactEmailAddress.trim() || undefined,
                    },
                },
            });
            onSaved();
            closeDialog();
        } finally {
            setCreating(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (nextOpen) onOpenChange(true);
                else closeDialog();
            }}
        >
            <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col gap-4 overflow-y-auto">
                <DialogHeader className="">
                    <DialogTitle>{t("admin.orgs.subOrgs.new")}</DialogTitle>
                </DialogHeader>
                <Grid cols={1}>
                    <ListCard
                        className="shrink-0"
                        title="Basic Information"
                        description="Essential information about the sub-organization"
                    >
                        <ListCardRow className="max-h-max">
                            <FieldSet className="w-full max-w-none">
                                <FieldGroup>
                                    <Field>
                                        <FieldLabel htmlFor="org-name" required>
                                            {t("admin.orgs.subOrgs.name")}
                                        </FieldLabel>
                                        <Input
                                            required
                                            id="org-name"
                                            value={organization.name}
                                            onChange={(e) =>
                                                setOrganization((prev) => ({ ...prev, name: e.target.value }))
                                            }
                                            type="text"
                                        />
                                    </Field>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Field>
                                            <FieldLabel htmlFor="sub-org-contact-email">
                                                {t("admin.orgs.subOrgs.contactEmail")}
                                            </FieldLabel>
                                            <Input
                                                id="sub-org-contact-email"
                                                type="email"
                                                value={organization.contactEmailAddress}
                                                onChange={(e) =>
                                                    setOrganization((prev) => ({
                                                        ...prev,
                                                        contactEmailAddress: e.target.value,
                                                    }))
                                                }
                                                placeholder="email@example.com"
                                            />
                                        </Field>
                                        <Field>
                                            <FieldLabel htmlFor="sub-org-contact-phone">
                                                {t("admin.orgs.subOrgs.contactPhone")}
                                            </FieldLabel>
                                            <Input
                                                id="sub-org-contact-phone"
                                                value={organization.contactPhoneNumber}
                                                onChange={(e) =>
                                                    setOrganization((prev) => ({
                                                        ...prev,
                                                        contactPhoneNumber: e.target.value,
                                                    }))
                                                }
                                                placeholder="(555) 555-5555"
                                            />
                                        </Field>
                                    </div>
                                </FieldGroup>
                            </FieldSet>
                        </ListCardRow>
                    </ListCard>
                </Grid>
                <DialogFooter>
                    <Button variant={"outline"} onClick={closeDialog}>
                        {createdOrgId ? t("common.actions.done") : t("common.actions.cancel")}
                    </Button>
                    <Button disabled={!organization.name.trim() || creating} onClick={() => void handleSave()}>
                        {creating ? "Saving…" : createdOrgId ? "Save changes" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
